import React, { useEffect, useState } from 'react';
import { Button } from '@amzn/stencil-react-components/button';
import { Flex, Row, View } from '@amzn/stencil-react-components/layout';
import { H2 } from '@amzn/stencil-react-components/text';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import useTemplateFormData from '../../hooks/useTemplateFormData';
import { ContentPanel } from './ContentPanel';
import { EditorPanel } from './EditorPanel';
import { ElementsPanel } from './ElementsPanel';
import TemplateSaveModal from './TemplateSaveModal';
import { useDispatch } from 'react-redux';
import { deleteMessageTemplate, resetTemplateForm } from '../../actions/nudgeActions';
import { FormType } from '../../enums/commonEnums';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { HOME_PAGE } from '../../constants/routesConstant';
import { ResponseStatus } from '../../enums/responseStatus';
import ButtonWithLoader from '../atoms/ButtonWithLoader';
import { FormattedMessage } from 'react-intl';
import { IconExclamationTriangle } from '../../mocks/stencilV4IconMocks';

const CreateContent = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const IconWithTooltip = withTooltip()(IconExclamationTriangle);
  const { isValid, formMode, templateName, deleteStatus } = useTemplateFormData();
  const isEditMode = formMode === FormType.EDIT;

  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const onSaveTemplate = () => {
    setOpenTemplateModal(true);
  };

  const deleteTemplate = async () => {
    setIsDeleting(true);
    await dispatch(deleteMessageTemplate(templateName));
    setIsDeleting(false);
  };
  // Reset the template form on component destroy
  useEffect(() => {
    return () => {
      dispatch(resetTemplateForm());
    };
  }, []);

  useEffect(
    () => {
      if (deleteStatus === ResponseStatus.SUCCESS) {
        dispatch(resetTemplateForm());
      }
    },
    [deleteStatus],
  );

  return (
    <Flex width={'99%'} data-testid="create-content" flexDirection="column" gridGap={'S200'}>
      <View justifyContent="center" minHeight={100}>
        <H2>
          <FormattedMessage
            id="app.createContent.label.create"
            defaultMessage="Create an email message"
          />
        </H2>
      </View>

      <View>
        <ElementsPanel />
      </View>

      <Flex flexDirection="row" gridGap={'S200'} alignItems="stretch">
        <ContentPanel />
        <EditorPanel />
      </Flex>

      <Flex>
        <View flex={1} padding={'30px'} backgroundColor="primary05" margin="0 0 0 -25px">
          <Row gridGap={'S300'} justifyContent="flex-end">
            <Button
              primary
              disabled={!isValid}
              data-testid="save-template-btn"
              onClick={() => onSaveTemplate()}
            >
              <Row alignItems="center">
                {isEditMode ? (
                  <FormattedMessage id="app.createContent.button.updateTemplate" />
                ) : (
                  <FormattedMessage id="app.createContent.button.saveTemplate" />
                )}
                {!isValid ? (
                  <IconWithTooltip
                    titleText="Show tooltip"
                    tooltipText="Please add a valid subject line and add atleast one element in the content to proceed"
                  />
                ) : null}
              </Row>
            </Button>
            {isEditMode ? (
              <ButtonWithLoader
                isDisabled={isDeleting}
                isLoading={isDeleting}
                isPrimary={true}
                onClickHandler={deleteTemplate}
              >
                <FormattedMessage id="app.createContent.button.deleteTemplate" />
              </ButtonWithLoader>
            ) : null}
            <Button
              tertiary
              onClick={() => {
                history.push(HOME_PAGE);
              }}
            >
              <FormattedMessage id="app.createContent.button.cancel" />
            </Button>
          </Row>
        </View>
      </Flex>
      <TemplateSaveModal isOpen={openTemplateModal} close={() => setOpenTemplateModal(false)} />
    </Flex>
  );
};

export default CreateContent;
